import styled from '@emotion/styled';
import { cloneElement, isValidElement, useMemo } from 'react';

import { Button } from '../Button';
import { DropdownMenu } from '../DropdownMenu';
import type { DropdownMenuItemOrDivider } from '../DropdownMenu';
import type { Action, ActionMenuButtonsProps, ActionMenuGapSize } from './ActionMenuButtons.types';

/**
 * Container for action menu buttons with configurable gap
 * Matches ib-ui MenuButtonsContainer styled component
 */
const MenuButtonsContainer = styled.div<{ $gapSize: ActionMenuGapSize }>`
  display: flex;
  align-items: center;

  .action-button {
    display: flex;
  }

  .dropdown-menu {
    display: flex;
    align-items: center;
  }

  ${({ $gapSize }) => {
    switch ($gapSize) {
      case 'xs':
        return `gap: 8px;`;
      case 'sm':
        return `gap: 12px;`;
      case 'lg':
        return `gap: 18px;`;
      case 'md':
      default:
        return `gap: 16px;`;
    }
  }}
`;

/**
 * Grey color for dropdown icons (matches ib-ui grey600)
 */
const GREY_600 = '#6b7280';

/**
 * Format aria-label from various input types
 * Matches ib-ui formatAriaLabel function
 */
function formatAriaLabel(ariaLabel: unknown, displayText: unknown): string {
  const ariaToUse = ariaLabel || displayText;

  if (typeof ariaToUse === 'string') {
    return ariaToUse;
  }

  return 'Action Menu Button';
}

/**
 * ActionMenuButtons displays a row of primary action buttons with an optional
 * dropdown menu for secondary actions.
 *
 * This component matches the ib-ui ActionMenuButtons API exactly.
 *
 * Primary actions are rendered as icon-only buttons.
 * Secondary actions appear in a dropdown menu.
 *
 * @example
 * ```tsx
 * <ActionMenuButtons
 *   actions={[
 *     { id: 'download', displayText: 'Download', icon: <DownloadIcon />, onClick: handleDownload },
 *     { id: 'add', displayText: 'Add', icon: <AddIcon />, onClick: handleAdd },
 *     { id: 'delete', displayText: 'Delete', icon: <DeleteIcon />, onClick: handleDelete },
 *   ]}
 *   primaryActions={['download', 'add']}
 * />
 * ```
 */
export function ActionMenuButtons({
  actions = [],
  dropdownMenuType,
  menuDataId,
  menuDisabled,
  primaryActions = [],
  showDropdownActionIcon = false,
  gapSize = 'sm',
  buttonSize = 'md',
  inverse = false,
  menuIcon,
  buttonVariant = 'ghost',
  buttonTabIndex,
  ...props
}: ActionMenuButtonsProps) {
  // Separate actions into primary (buttons) and secondary (dropdown)
  const { primaryMenuButtons, secondaryMenuButtons } = useMemo(() => {
    const primary = actions.filter(({ id }) => primaryActions.includes(id));
    const secondary = actions.filter(({ id }) => !primaryActions.includes(id));
    return { primaryMenuButtons: primary, secondaryMenuButtons: secondary };
  }, [primaryActions, actions]);

  // Convert secondary actions to dropdown menu items
  const dropdownItems: DropdownMenuItemOrDivider[] = useMemo(() => {
    return secondaryMenuButtons.map((item) => {
      // Clone icon with grey color if showDropdownActionIcon is true
      let iconToUse = undefined;
      if (showDropdownActionIcon && item.icon && isValidElement(item.icon)) {
        iconToUse = cloneElement(item.icon as React.ReactElement<{ color?: string }>, {
          color: GREY_600,
        });
      } else if (showDropdownActionIcon && item.icon) {
        iconToUse = item.icon;
      }

      return {
        id: item.id,
        displayText: item.displayText,
        icon: iconToUse,
        onClick: item.onClick,
        disabled: item.disabled,
        selected: item.selected,
        value: item.value,
        divider: item.divider,
        'data-testid': item.dataTestId,
      };
    });
  }, [secondaryMenuButtons, showDropdownActionIcon]);

  // Create custom trigger if menuIcon is provided
  const customTrigger = useMemo(() => {
    if (!menuIcon) return undefined;

    return (
      <Button
        icon={menuIcon}
        variant={inverse ? 'ghostInverse' : 'ghost'}
        size={buttonSize}
        disabled={menuDisabled}
        aria-label="Toggle menu"
        tabIndex={buttonTabIndex}
      />
    );
  }, [menuIcon, inverse, buttonSize, menuDisabled, buttonTabIndex]);

  // Return null if no actions
  if (actions.length === 0) {
    return null;
  }

  return (
    <MenuButtonsContainer data-testid="primary-actions" $gapSize={gapSize} {...props}>
      {/* Primary action buttons - icon only */}
      {primaryMenuButtons.map(
        (
          { icon, dataId, disabled, onClick, tooltip, ariaLabel, dataTestId, displayText }: Action,
          index: number,
        ) => (
          <Button
            key={`${dataId}-${index}`}
            disabled={disabled || menuDisabled}
            icon={icon}
            variant={buttonVariant}
            data-id={dataId}
            data-testid={dataTestId}
            tooltip={tooltip}
            onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
            size={buttonSize}
            aria-label={formatAriaLabel(ariaLabel, displayText)}
            tabIndex={buttonTabIndex}
          />
        ),
      )}

      {/* Secondary actions dropdown */}
      {secondaryMenuButtons.length > 0 && (
        <DropdownMenu
          className="dropdown-menu"
          disabled={menuDisabled}
          menuButtonDataId={menuDataId}
          inverse={inverse}
          buttonSize={buttonSize}
          buttonTabIndex={buttonTabIndex}
          items={dropdownItems}
          type={dropdownMenuType}
          trigger={customTrigger}
        />
      )}
    </MenuButtonsContainer>
  );
}

export default ActionMenuButtons;
